import type { WebhookPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Link from '@/components/link';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { isPersistedStatus } from './forms/webhookSchema';

export type WebhookTableFields = WebhookPartsFragment & {
  CreatedByUserName: null | string;
  EventTypeCount: number;
  [key: string]: unknown;
};

export const useGetCollectionTableProps = (
  records: WebhookPartsFragment[],
  onEdit?: (webhook: WebhookPartsFragment) => void
): TablePropsWithActions<WebhookTableFields> => {
  const { t } = useTranslation(['common'], { keyPrefix: 'webhooks' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'webhooks.columns',
  });
  const { t: stat } = useTranslation(['common'], {
    keyPrefix: 'webhooks.status',
  });

  const labelledFields = useMemo<WebhookTableFields[]>(
    () =>
      records.map((record) => ({
        ...record,
        CreatedByUserName: record.createdByUser?.FriendlyName ?? null,
        EventTypeCount: record.subscriptions.length,
      })),
    [records]
  );

  const fields: TableFields<WebhookTableFields> = {
    Title: {
      header: st('name'),
      cell: (item) =>
        onEdit ? (
          <Link variant={'secondary'} href={'#'} onFollow={() => onEdit(item)}>
            {item.Title}
          </Link>
        ) : (
          item.Title
        ),
    },
    WebhookUrl: {
      header: st('url'),
    },
    Status: {
      header: st('status'),
      cell: (c) => (isPersistedStatus(c.Status) ? stat(c.Status) : c.Status),
    },
    EventTypeCount: {
      header: st('eventTypes'),
      fieldType: 'number',
    },
    CreatedByUserName: {
      header: st('createdByUser'),
    },
    CreatedAtTimestamp: dateColumnFromConfig({
      header: { header: st('createdAtTimestamp') },
      dateField: 'CreatedAtTimestamp',
    }),
  };

  return useGetTableProps({
    tableId: 'organisationWebhooksRegister',
    data: labelledFields,
    entityLabel: t('entity_name'),
    fields,
    initialColumns: [
      'Title',
      'WebhookUrl',
      'Status',
      'EventTypeCount',
      'CreatedByUserName',
      'CreatedAtTimestamp',
    ],
    preferencesStorageKey: 'OrganisationWebhooksSettingsTable-Preferences',
    customAttributeFormIds: [],
  });
};
